import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface SubscriptionData {
  plan: { code: string; name: string; priceVnd: number };
  status: 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';
  currentPeriodEnd: string;
  autoRenew: boolean;
  pendingInvoice: { id: string; amountVnd: number; dueAt: string; paymentRef: string } | null;
  nudge: { kind: 'RENEWAL_DUE' | 'PAST_DUE' | 'EXPIRED'; dueAt: string; daysLeft: number } | null;
}

function authHeaders(token: string | null): Record<string, string> {
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

export function useSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscription'],
    queryFn: async (): Promise<SubscriptionData> => {
      const res = await fetch(`${API}/subscription`, { headers: authHeaders(token) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed to load subscription');
      return json.data;
    },
    enabled: !!token,
  });
}

export function useUpgradeSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planCode: 'PRO' | 'BUSINESS') => {
      const res = await fetch(`${API}/subscription/upgrade`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ planCode }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Upgrade failed');
      return json.data as {
        subscriptionInvoiceId: string;
        amountVnd: number;
        paymentRef: string;
        qrUrl: string | null;
        bankInfo: { bin: string; accountNo: string; accountName: string };
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useCancelSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Cancel failed');
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useResumeSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/subscription/resume`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Resume failed');
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useSubscriptionInvoices() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscription', 'invoices'],
    queryFn: async () => {
      const res = await fetch(`${API}/subscription/invoices?limit=50`, {
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed');
      return json.data.items as Array<{
        id: string;
        status: string;
        amountVnd: number;
        paymentRef: string;
        periodStart: string;
        periodEnd: string;
        paidAt: string | null;
        createdAt: string;
      }>;
    },
    enabled: !!token,
  });
}

export function useSubscriptionInvoice(id: string | undefined) {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscription', 'invoices', id],
    queryFn: async () => {
      const res = await fetch(`${API}/subscription/invoices/${id}`, {
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed');
      return json.data;
    },
    enabled: !!token && !!id,
  });
}
